import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, ShoppingCart, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useNotifications } from "../contexts/NotificationContext";

const ACCENT = "linear-gradient(135deg, #6f12ff 0%, #9c27ff 100%)";
const DELIVERY_FEE = 5;
const OTHER_FEES = 3;

const pulseStyle = `
  @keyframes cartPulse {
    0% { opacity: 1; }
    50% { opacity: 0.42; }
    100% { opacity: 1; }
  }
`;

const shellStyle = {
  minHeight: "100vh",
  background: "#f3f0f8",
  padding: "20px 24px 40px",
};

const containerStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
};

const headerStyle = {
  display: "grid",
  gridTemplateColumns: "34px 1fr 34px",
  alignItems: "center",
  marginBottom: 28,
};

const shimmerCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 14,
  borderRadius: 20,
  background: "#f3f0f8",
  marginBottom: 12,
  animation: "cartPulse 1.5s infinite",
};

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getMedicineId = (medicine) => {
  if (!medicine) return null;
  return typeof medicine === "object" ? medicine._id : medicine;
};

const normalizeCartResponse = (prevCart, nextCart) => {
  const safePrev = prevCart && typeof prevCart === "object" ? prevCart : { items: [], totalPrice: 0 };
  const safeNext = nextCart && typeof nextCart === "object" ? nextCart : { items: [], totalPrice: 0 };
  const nextItems = Array.isArray(safeNext.items) ? safeNext.items : [];

  const prevByItemId = new Map(
    (Array.isArray(safePrev.items) ? safePrev.items : []).map((item) => [String(item?._id || ""), item])
  );

  const prevByMedicineId = new Map(
    (Array.isArray(safePrev.items) ? safePrev.items : []).map((item) => [String(getMedicineId(item?.medicine) || ""), item])
  );

  const mergedItems = nextItems.map((item) => {
    const itemIdKey = String(item?._id || "");
    const medicineIdKey = String(getMedicineId(item?.medicine) || "");
    const prevMatch = prevByItemId.get(itemIdKey) || prevByMedicineId.get(medicineIdKey);

    const nextMedicineObject = typeof item?.medicine === "object" ? item.medicine : null;
    const prevMedicineObject = typeof prevMatch?.medicine === "object" ? prevMatch.medicine : null;

    const mergedMedicine = nextMedicineObject
      ? { ...(prevMedicineObject || {}), ...nextMedicineObject }
      : (prevMedicineObject || item?.medicine);

    return {
      ...(prevMatch || {}),
      ...item,
      medicine: mergedMedicine,
    };
  });

  return {
    ...safePrev,
    ...safeNext,
    items: mergedItems,
  };
};

const getItemMeta = (medicine = {}) => medicine.category || medicine.type || medicine.brand || "4 oz refill";

const getItemSubMeta = (item) => {
  const unit = item?.medicine?.unit || item?.medicine?.dose || "12 glass";
  return `${unit} • ${getItemMeta(item?.medicine)}`;
};

const SkeletonCard = () => (
  <div style={shimmerCardStyle}>
    <div style={{ width: 58, height: 58, borderRadius: 16, background: "#e4dff0" }} />
    <div style={{ flex: 1 }}>
      <div style={{ width: "58%", height: 12, borderRadius: 999, background: "#e4dff0", marginBottom: 8 }} />
      <div style={{ width: "42%", height: 10, borderRadius: 999, background: "#ebe7f4", marginBottom: 10 }} />
      <div style={{ width: "28%", height: 10, borderRadius: 999, background: "#ebe7f4" }} />
    </div>
  </div>
);

const SummaryRow = ({ label, amount, highlight = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
    <span style={{ fontSize: 11, color: highlight ? "#16141d" : "#817a8e", fontWeight: highlight ? 700 : 500 }}>{label}</span>
    <span style={{ fontSize: 11, color: "#16141d", fontWeight: highlight ? 800 : 600 }}>{formatMoney(amount)}</span>
  </div>
);

export default function CartPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], totalPrice: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const { addNotification } = useNotifications();

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await api.get("/api/cart");
        setCart((prev) => normalizeCartResponse(prev, res.data));
      } catch (err) {
        console.error("Failed to fetch cart:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, []);

  const updateQuantity = async (itemId, quantity) => {
    if (quantity < 1) return;

    try {
      setUpdating(true);
      const res = await api.patch(`/api/cart/update/${itemId}`, { quantity }, { withCredentials: true });
      setCart((prev) => normalizeCartResponse(prev, res.data));
    } catch (err) {
      console.error("Failed to update quantity:", err);
    } finally {
      setUpdating(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      setUpdating(true);
      const res = await api.delete(`/api/cart/remove/${itemId}`);
      setCart((prev) => normalizeCartResponse(prev, res.data));
    } catch (err) {
      console.error("Failed to remove item:", err);
    } finally {
      setUpdating(false);
    }
  };

  const clearCart = async () => {
    try {
      setUpdating(true);
      await api.delete("/api/cart/clear");
      setCart({ items: [], totalPrice: 0 });
      addNotification({
        type: "cart_clear",
        title: "Cart Cleared",
        body: "All items have been removed from your pharmacy cart",
        icon: "🗑️",
      });
    } catch (err) {
      console.error("Failed to clear cart:", err);
    } finally {
      setUpdating(false);
    }
  };

  const goToCheckout = () => {
    if (cart.items.length === 0) {
      alert("Your cart is empty");
      return;
    }

    const pharmacyId = cart.items[0]?.medicine?.pharmacy?._id || cart.items[0]?.medicine?.pharmacy;

    navigate("/checkout", {
      state: {
        cartItems: cart.items.map((item) => ({
          _id: item.medicine?._id,
          name: item.medicine?.name,
          price: item.price,
          quantity: item.quantity,
          image: item.medicine?.images?.[0] || "",
          meta: getItemMeta(item.medicine),
        })),
        totalPrice: cart.totalPrice,
        pharmacyId,
      },
    });
  };

  const subtotal = cart.totalPrice || 0;
  const taxes = 0;
  const total = subtotal + taxes + OTHER_FEES + DELIVERY_FEE;

  return (
    <div style={shellStyle}>
      <style>{pulseStyle}</style>

      <div style={containerStyle}>
        <div style={headerStyle}>
            <i
              className="fi fi-br-angle-left"
              onClick={() => navigate(-1)}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{
                fontSize: "20px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                color: "#2d2536",
                padding: "6px 10px",
                cursor: "pointer",
                display: "inline-flex",
              }}
            ></i>

            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#18141f", textAlign: "center" }}>Cart</h1>

            <div style={{ position: "relative", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingCart size={18} color="#2d2536" />
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  right: 1,
                  minWidth: 14,
                  height: 14,
                  borderRadius: 999,
                  background: "#ff4b6e",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 3px",
                }}
              >
                {cart.items.length}
              </span>
            </div>
          </div>

          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : cart.items.length === 0 ? (
            <div style={{ padding: "40px 14px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: 86,
                  height: 86,
                  borderRadius: 28,
                  margin: "0 auto 16px",
                  background: "linear-gradient(180deg, #f3e8ff 0%, #ede9fe 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShoppingCart size={32} color="#7b1cff" />
              </div>
              <h2 style={{ margin: "0 0 8px", fontSize: 20, color: "#18141f" }}>Your cart is empty</h2>
              <p style={{ margin: "0 0 22px", color: "#7f788d", fontSize: 14 }}>Add medications to start building your pharmacy order.</p>
              <button
                type="button"
                onClick={() => navigate("/epharmacy")}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 16,
                  padding: "15px 18px",
                  color: "#fff",
                  fontWeight: 700,
                  background: ACCENT,
                  boxShadow: "0 18px 28px rgba(124, 58, 237, 0.28)",
                  cursor: "pointer",
                }}
              >
                Go Shopping
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gap: 14 }}>
                {cart.items.map((item) => (
                  <div
                    key={item._id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "60px 1fr auto",
                      gap: 14,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: 18,
                      background: "#ffffff",
                      border: "1px solid #f0ecf7",
                      boxShadow: "0 6px 18px rgba(77, 30, 126, 0.07)",
                    }}
                  >
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 16,
                        overflow: "hidden",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #f3eff8",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={item.medicine?.images?.[0] || "https://via.placeholder.com/80x80?text=Rx"}
                        alt={item.medicine?.name || "Medicine"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#18141f", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.medicine?.name || "Medicine"}
                      </div>
                      <div style={{ fontSize: 10, color: "#938aa4", marginBottom: 6 }}>
                        {getItemSubMeta(item)}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#18141f" }}>{formatMoney(item.price * item.quantity)}</div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item._id, item.quantity - 1)}
                          disabled={updating || item.quantity <= 1}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 7,
                            border: "1px solid #ece7f4",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#9b93ab",
                            cursor: "pointer",
                          }}
                        >
                          <Minus size={12} />
                        </button>
                        <span style={{ minWidth: 10, textAlign: "center", fontSize: 12, fontWeight: 700, color: "#2d2536" }}>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          disabled={updating}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 7,
                            border: "none",
                            background: ACCENT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            cursor: "pointer",
                            boxShadow: "0 8px 14px rgba(124, 58, 237, 0.2)",
                          }}
                        >
                          <Plus size={12} />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item._id)}
                        disabled={updating}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#c25a6a",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 10,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => navigate("/epharmacy")}
                style={{
                  marginTop: 6,
                  width: "100%",
                  padding: "11px 12px",
                  borderRadius: 12,
                  border: "1px solid #eee8f7",
                  background: "#fff",
                  color: "#2d2536",
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                + Add Items
              </button>

              <div
                style={{
                  marginTop: 28,
                  padding: "18px 18px 14px",
                  borderRadius: 18,
                  background: "#ffffff",
                  border: "1px solid #f0ecf7",
                  boxShadow: "0 6px 18px rgba(77, 30, 126, 0.07)",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "#18141f", marginBottom: 12 }}>Cost summary</div>
                <SummaryRow label="Subtotal" amount={subtotal} />
                <SummaryRow label="Taxes" amount={taxes} />
                <SummaryRow label="Other Fees" amount={OTHER_FEES} />
                <SummaryRow label="Delivery Fees" amount={DELIVERY_FEE} />
                <div style={{ height: 1, background: "#ece7f4", margin: "12px 0" }} />
                <SummaryRow label="Total cost" amount={total} highlight />
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                <button
                  type="button"
                  onClick={clearCart}
                  disabled={updating}
                  style={{
                    flex: "0 0 110px",
                    borderRadius: 14,
                    border: "1px solid #f0e5e8",
                    background: "#fff5f7",
                    color: "#ba5668",
                    fontWeight: 700,
                    fontSize: 12,
                    minHeight: 48,
                    cursor: "pointer",
                  }}
                >
                  {updating ? "Clearing" : "Clear"}
                </button>

                <button
                  type="button"
                  onClick={goToCheckout}
                  disabled={updating}
                  style={{
                    flex: 1,
                    border: "none",
                    borderRadius: 14,
                    padding: "15px 18px",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    background: ACCENT,
                    boxShadow: "0 18px 24px rgba(124, 58, 237, 0.28)",
                    cursor: "pointer",
                  }}
                >
                  Place Order
                </button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
